import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AcademicDetailsSection = ({ academicData, onAdd, onUpdate, onDelete }) => {
  const [expandedCard, setExpandedCard] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [newEntry, setNewEntry] = useState({
    degree: '',
    institution: '',
    specialization: '',
    startYear: '',
    endYear: '',
    grade: '',
    gradeType: 'percentage'
  });

  const gradeTypeOptions = [
    { value: 'percentage', label: 'Percentage' },
    { value: 'cgpa', label: 'CGPA' },
    { value: 'gpa', label: 'GPA' }
  ];

  const handleAddNew = () => {
    if (newEntry?.degree && newEntry?.institution && newEntry?.grade) {
      onAdd(newEntry);
      setNewEntry({
        degree: '',
        institution: '',
        specialization: '',
        startYear: '',
        endYear: '',
        grade: '',
        gradeType: 'percentage'
      });
      setIsAdding(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg md:text-xl font-heading font-semibold text-card-foreground">
          Academic Qualifications
        </h3>
        <Button
          variant="outline"
          size="sm"
          iconName="Plus"
          iconPosition="left"
          onClick={() => setIsAdding(!isAdding)}
        >
          Add Qualification
        </Button>
      </div>
      {isAdding && (
        <div className="bg-muted/50 rounded-lg p-4 md:p-6 border border-border space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Degree/Qualification"
              type="text"
              placeholder="B.Tech, B.Sc, MBA, etc."
              value={newEntry?.degree}
              onChange={(e) => setNewEntry({ ...newEntry, degree: e?.target?.value })}
              required
            />
            <Input
              label="Institution Name"
              type="text"
              placeholder="University/College name"
              value={newEntry?.institution}
              onChange={(e) => setNewEntry({ ...newEntry, institution: e?.target?.value })}
              required
            />
          </div>

          <Input
            label="Specialization/Branch"
            type="text"
            placeholder="Computer Science, Commerce, etc."
            value={newEntry?.specialization}
            onChange={(e) => setNewEntry({ ...newEntry, specialization: e?.target?.value })}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Start Year"
              type="text"
              placeholder="2020"
              value={newEntry?.startYear}
              onChange={(e) => setNewEntry({ ...newEntry, startYear: e?.target?.value })}
            />
            <Input
              label="End Year"
              type="text"
              placeholder="2024"
              value={newEntry?.endYear}
              onChange={(e) => setNewEntry({ ...newEntry, endYear: e?.target?.value })}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Grade Type"
              options={gradeTypeOptions}
              value={newEntry?.gradeType}
              onChange={(value) => setNewEntry({ ...newEntry, gradeType: value })}
            />
            <Input
              label="Grade/Score"
              type="text"
              placeholder="85.5"
              value={newEntry?.grade}
              onChange={(e) => setNewEntry({ ...newEntry, grade: e?.target?.value })}
              required
            />
          </div>

          <div className="flex gap-3">
            <Button variant="default" onClick={handleAddNew}>
              Save Qualification
            </Button>
            <Button variant="outline" onClick={() => setIsAdding(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      <div className="space-y-4">
        {academicData?.map((item, index) => (
          <div
            key={index}
            className="bg-card rounded-lg border border-border overflow-hidden transition-smooth hover:shadow-warm-md"
          >
            <div
              className="p-4 md:p-6 cursor-pointer"
              onClick={() => setExpandedCard(expandedCard === index ? null : index)}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name="GraduationCap" size={20} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h4 className="text-base md:text-lg font-semibold text-card-foreground">
                        {item?.degree}
                      </h4>
                      <p className="text-sm text-muted-foreground">{item?.institution}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground ml-13">
                    <span className="flex items-center gap-1">
                      <Icon name="Calendar" size={14} />
                      {item?.startYear} - {item?.endYear}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="Award" size={14} />
                      {item?.grade} {item?.gradeType === 'percentage' ? '%' : item?.gradeType?.toUpperCase()}
                    </span>
                  </div>
                </div>
                <Icon
                  name={expandedCard === index ? 'ChevronUp' : 'ChevronDown'}
                  size={20}
                  color="var(--color-muted-foreground)"
                />
              </div>
            </div>

            {expandedCard === index && (
              <div className="px-4 md:px-6 pb-4 md:pb-6 border-t border-border">
                <div className="pt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-card-foreground">Specialization:</span>
                    <span className="text-sm text-muted-foreground">{item?.specialization || 'Not specified'}</span>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Edit"
                      iconPosition="left"
                      onClick={() => onUpdate(index)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      iconName="Trash2"
                      iconPosition="left"
                      onClick={() => onDelete(index)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {academicData?.length === 0 && !isAdding && (
        <div className="text-center py-12 bg-muted/30 rounded-lg border border-dashed border-border">
          <Icon name="GraduationCap" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
          <p className="text-muted-foreground mb-4">No academic qualifications added yet</p>
          <Button variant="outline" onClick={() => setIsAdding(true)}>
            Add Your First Qualification
          </Button>
        </div>
      )}
    </div>
  );
};

export default AcademicDetailsSection;